import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { api } from '../api/client';
import { auth as authApi } from '../api';
import type { User } from '../types';
import React from 'react';

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (email: string) => Promise<{ token: string }>;
  verify: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = api.getToken();
    if (token) {
      authApi.me().then(setUser).catch(() => api.setToken(null)).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = useCallback(async (email: string) => {
    const result = await authApi.sendMagicLink(email);
    return { token: result.token || '' };
  }, []);

  const verify = useCallback(async (token: string) => {
    const result = await authApi.verify(token);
    api.setToken(result.session_token);
    setUser(result.user);
  }, []);

  const logout = useCallback(async () => {
    try { await authApi.logout(); } catch { /* ignore */ }
    api.setToken(null);
    setUser(null);
  }, []);

  const value: AuthState = { user, loading, login, verify, logout, isAuthenticated: !!user };

  return React.createElement(AuthContext.Provider, { value }, children);
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
